// Raster grading panel (docs/05-raster-grading.md) — shown in the review
// details column, split into two card sections styled like the annotation
// cards: a Language section (Lexik / Grammatik / Textgestaltung) and a Content
// section (one card per Exam task). Each card shows its weight-applied combined
// NP on the right and expands (▾) to the reasoning behind the deterministic
// suggestion:
//   • language → the error-to-word ratio + the curve mapping, and the
//     contributing LanguageTool flags (each deletable);
//   • content  → Claude's flagged evidence passages (each deletable),
//     highlighted in the scan.
// Deleting a reason dismisses the underlying annotation so its deduction drops
// and the suggested score re-derives automatically (no new LLM call); it can be
// restored. Each per-subcriterion 0–15 rating slot (with a reset-to-suggestion
// control) expands to Claude's per-subcriterion reasoning — plus deletable
// evidence excerpts for content. The criteria area scrolls; the score area
// (ScoreDisplay) stays pinned by the parent layout.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { apiJson } from "../api/client";
import { usePaperRatings } from "../api/hooks";
import type {
  Annotation,
  ContentCriterionReasoning,
  CriterionRating,
  LanguageExplanation,
  Paper,
  Project,
  RatingsResponse,
} from "../api/types";
import { useAnnotationStore } from "../state/annotationStore";

import "./RasterPanel.css";

interface Props {
  project: Project;
  paper: Paper;
}

interface SaveBody {
  criterion_key: string;
  task_id: number | null;
  points_manual?: number | null;
  note?: string | null;
}

/** The owning criterion key of a subcriterion node key ("lang.lexik::0.0"). */
function critKeyOf(subKey: string): string {
  return subKey.split("::")[0];
}

export function RasterPanel({ paper }: Props) {
  const { t } = useTranslation();
  const { data, mutate } = usePaperRatings(paper.id);
  const annotations = useAnnotationStore((s) => s.annotations);
  const setHighlight = useAnnotationStore((s) => s.setHighlight);
  const setFrameRegion = useAnnotationStore((s) => s.setFrameRegion);
  const select = useAnnotationStore((s) => s.select);

  const annSignature = annotations.map((a) => `${a.id}:${a.status}`).join(",");
  useEffect(() => {
    void mutate();
  }, [annSignature, mutate]);

  // Drop the scan highlight + task frame when the panel unmounts (e.g. back to
  // cards view).
  useEffect(() => () => { setHighlight(null); setFrameRegion(null); }, [setHighlight, setFrameRegion]);

  const annById = useMemo(() => {
    const m = new Map<number, Annotation>();
    for (const a of annotations) m.set(a.id, a);
    return m;
  }, [annotations]);

  const ratings = data?.ratings ?? [];
  const result = data?.result ?? null;
  const tasks = data?.tasks ?? [];
  const explanations = data?.explanations;
  const subtotals = result?.subtotals;

  // One reasoning panel open at a time so the scan highlight is unambiguous.
  const [openKey, setOpenKey] = useState<string | null>(null);
  function toggleReason(key: string, evidenceIds: number[]) {
    const next = openKey === key ? null : key;
    setOpenKey(next);
    if (next === null) {
      setHighlight(null);
    } else {
      setHighlight(evidenceIds);
      if (evidenceIds.length) select(evidenceIds[0]);
    }
  }

  async function saveRating(body: SaveBody) {
    const fresh = await apiJson<RatingsResponse>(`/api/papers/${paper.id}/ratings`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
    await mutate(fresh, { revalidate: false });
  }

  // Teacher disable/enable of a task — drops it from / returns it to the score
  // (docs/05 §2); the grade recomputes.
  async function saveChoice(taskId: number, attempted: boolean) {
    const fresh = await apiJson<RatingsResponse>(`/api/papers/${paper.id}/task_choice`, {
      method: "PUT",
      body: JSON.stringify({ task_id: taskId, attempted }),
    });
    await mutate(fresh, { revalidate: false });
  }

  // Reset every overridden sub-criterion of one card back to its suggestion.
  async function resetCard(cardRows: CriterionRating[]) {
    for (const r of cardRows) {
      if (r.points_manual != null) {
        await saveRating({ criterion_key: r.criterion_key, task_id: r.task_id, points_manual: null });
      }
    }
  }

  const langRows = ratings.filter((r) => r.group === "language");
  const contentRows = ratings.filter((r) => r.group === "content");

  // Language: one card per criterion (Lexik / Grammatik / Textgestaltung).
  const langCritKeys = dedupe(langRows.map((r) => critKeyOf(r.criterion_key)));

  return (
    <section className="raster-panel" data-tour="raster-panel">
      <div className="raster-section">
        <div className="raster-section__heading">{t("raster.group_language")}</div>
        {langCritKeys.map((critKey) => {
          const rows = langRows.filter((r) => critKeyOf(r.criterion_key) === critKey);
          const exp = explanations?.language?.[critKey];
          return (
            <LanguageCriterionCard
              key={critKey}
              title={rows[0]?.parent_label ?? critKey}
              combined={subtotals?.language?.[critKey] ?? null}
              processed={paper.language_done}
              rows={rows}
              explanation={exp}
              annById={annById}
              open={openKey === `lang:${critKey}`}
              onToggle={() => { setFrameRegion(null); toggleReason(`lang:${critKey}`, exp?.evidence_ids ?? []); }}
              onPick={(id) => select(id)}
              onSave={saveRating}
              openKey={openKey}
              onToggleSub={toggleReason}
              onResetAll={() => resetCard(rows)}
            />
          );
        })}
      </div>

      <div className="raster-section">
        <div className="raster-section__heading">{t("raster.group_content")}</div>
        {tasks.length === 0 ? (
          <div className="raster-section__empty">{t("raster.no_tasks")}</div>
        ) : (
          tasks.map((task) => {
            const rows = contentRows.filter((r) => r.task_id === task.id);
            const choiceGroup = (task.choice_group ?? "").trim();
            // Mandatory tasks with no rows stay hidden (as before); a choice-group
            // task, or any task the teacher disabled, always shows so its
            // disable/enable button stays reachable.
            if (rows.length === 0 && !choiceGroup && task.counted !== false) return null;
            const title = task.label || task.operator || task.criterion_key;
            const exp = explanations?.content?.[String(task.id)] ?? [];
            const evIds = exp.flatMap((e) => e.evidence_ids);
            const taskOpen = openKey === `task:${task.id}`;
            const region = task.answer_region ?? null;
            return (
              <ContentTaskCard
                key={task.id}
                title={title}
                combined={subtotals?.content?.[String(task.id)] ?? null}
                processed={paper.content_done}
                rows={rows}
                reasoning={exp}
                annById={annById}
                open={taskOpen}
                onToggle={() => {
                  // Clicking the task frames its answer region on the scan (and
                  // scrolls there); clicking again clears it.
                  setFrameRegion(taskOpen ? null : region);
                  toggleReason(`task:${task.id}`, evIds);
                }}
                onPick={(id) => select(id)}
                onSave={saveRating}
                openKey={openKey}
                onToggleSub={toggleReason}
                choiceGroup={choiceGroup || null}
                counted={task.counted !== false}
                onSetDisabled={(disabled) => saveChoice(task.id, !disabled)}
                onResetAll={() => resetCard(rows)}
              />
            );
          })
        )}
      </div>
    </section>
  );
}

function dedupe(xs: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of xs) if (!seen.has(x)) { seen.add(x); out.push(x); }
  return out;
}

function npLabel(np: number | null | undefined): string {
  return np == null ? "—" : String(Math.round(np * 10) / 10);
}

// Card header: chevron toggle, the criterion/task name, and the weight-applied
// combined NP pill on the right.
function CardHead({
  title,
  combined,
  open,
  onToggle,
  overridden = false,
  onResetAll,
  children,
}: {
  title: string;
  combined: number | null;
  open: boolean;
  onToggle: () => void;
  overridden?: boolean;
  onResetAll?: () => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="raster-card__head">
      <button type="button" className="raster-card__headbtn" onClick={onToggle} aria-expanded={open}>
        <span className="raster-card__chevron">{open ? "▼" : "▶"}</span>
        <span className="raster-card__type">{title}</span>
      </button>
      {children}
      {overridden && onResetAll && (
        <button
          type="button"
          className="raster-card__resetall"
          title={t("raster.reset_all_suggested")}
          aria-label={t("raster.reset_all_suggested")}
          onClick={() => onResetAll()}
        >
          ↺
        </button>
      )}
      <span className="raster-card__np">
        {npLabel(combined)}
        <small>{t("score.np_unit")}</small>
      </span>
    </div>
  );
}

// A language criterion card: header + reasoning (ratio + curve + deletable
// flags) + the per-subcriterion rating slots.
function LanguageCriterionCard({
  title,
  combined,
  processed,
  rows,
  explanation,
  annById,
  open,
  onToggle,
  onPick,
  onSave,
  openKey,
  onToggleSub,
  onResetAll,
}: {
  title: string;
  combined: number | null;
  processed: boolean;
  rows: CriterionRating[];
  explanation: LanguageExplanation | undefined;
  annById: Map<number, Annotation>;
  open: boolean;
  onToggle: () => void;
  onPick: (id: number) => void;
  onSave: (body: SaveBody) => Promise<void>;
  openKey: string | null;
  onToggleSub: (key: string, evidenceIds: number[]) => void;
  onResetAll: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const overridden = rows.some((r) => r.points_manual != null);
  return (
    <div className={"raster-card" + (open ? " raster-card--open" : "")}>
      <CardHead
        title={title}
        combined={combined}
        open={open}
        onToggle={onToggle}
        overridden={overridden}
        onResetAll={onResetAll}
      />

      {open && (
        <div className="raster-reason">
          {!processed ? (
            <div className="raster-reason__none">{t("raster.not_processed")}</div>
          ) : explanation ? (
            <>
              <div className="raster-reason__title">{t("raster.reason_title")}</div>
              <dl className="raster-reason__stats">
                <div><dt>{t("raster.words_label")}</dt><dd>{explanation.words}</dd></div>
                <div><dt>{t("raster.load_label")}</dt><dd>{npLabel(explanation.load)}</dd></div>
                <div><dt>{t("raster.ratio_label")}</dt><dd>{npLabel(explanation.ratio_per_100)}</dd></div>
                <div><dt>{t("raster.mapped_np")}</dt><dd>{npLabel(explanation.suggested_np)}</dd></div>
              </dl>
              {explanation.evidence_ids.length > 0 ? (
                <>
                  <div className="raster-reason__ev-title">{t("raster.lang_evidence_title")}</div>
                  <ul className="raster-reason__ev">
                    {explanation.evidence_ids.map((id) => {
                      const a = annById.get(id);
                      if (!a) return null;
                      return <LanguageEvidence key={id} annotation={a} onPick={() => onPick(id)} />;
                    })}
                  </ul>
                </>
              ) : (
                <div className="raster-reason__none">{t("raster.evidence_none")}</div>
              )}
            </>
          ) : (
            <div className="raster-reason__none">{t("raster.evidence_none")}</div>
          )}
        </div>
      )}

      <div className="raster-card__body">
        {rows.map((r) => {
          const subKey = `sub:lang:${r.criterion_key}`;
          return (
            <SubRow
              key={r.criterion_key}
              rating={r}
              onSave={onSave}
              reasoning={r.reasoning ?? null}
              open={openKey === subKey}
              onToggle={() => onToggleSub(subKey, [])}
            />
          );
        })}
      </div>
    </div>
  );
}

// A content task card: header + reasoning (Claude's deletable evidence) + the
// per-subcriterion subcards (a Sprachmittlung task spans several criteria).
function ContentTaskCard({
  title,
  combined,
  processed,
  rows,
  reasoning,
  annById,
  open,
  onToggle,
  onPick,
  onSave,
  openKey,
  onToggleSub,
  choiceGroup,
  counted,
  onSetDisabled,
  onResetAll,
}: {
  title: string;
  combined: number | null;
  processed: boolean;
  rows: CriterionRating[];
  reasoning: ContentCriterionReasoning[];
  annById: Map<number, Annotation>;
  open: boolean;
  onToggle: () => void;
  onPick: (id: number) => void;
  onSave: (body: SaveBody) => Promise<void>;
  openKey: string | null;
  onToggleSub: (key: string, evidenceIds: number[]) => void;
  choiceGroup: string | null;
  counted: boolean;
  onSetDisabled: (disabled: boolean) => Promise<void>;
  onResetAll: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const parents = dedupe(rows.map((r) => r.parent_label));
  const multi = parents.length > 1;
  const overridden = rows.some((r) => r.points_manual != null);
  // Per-sub-criterion reasoning + evidence, so each sub-row can expand to its own
  // Claude assessment (the task card above still lists them all — "keep in both").
  const reasonByKey = useMemo(() => {
    const m = new Map<string, ContentCriterionReasoning>();
    for (const c of reasoning) m.set(c.criterion_key, c);
    return m;
  }, [reasoning]);
  return (
    <div className={"raster-card" + (open ? " raster-card--open" : "") + (counted ? "" : " raster-card--excluded")}>
      <CardHead
        title={title}
        combined={combined}
        open={open}
        onToggle={onToggle}
        overridden={overridden && counted}
        onResetAll={onResetAll}
      >
        {choiceGroup && (
          <span className="raster-choice-chip">{t("raster.choice_group", { group: choiceGroup })}</span>
        )}
        <button
          type="button"
          className={"raster-card__disable" + (counted ? "" : " raster-card__disable--off")}
          disabled={saving}
          title={counted ? t("raster.disable_task") : t("raster.enable_task")}
          aria-label={counted ? t("raster.disable_task") : t("raster.enable_task")}
          onClick={async () => { setSaving(true); try { await onSetDisabled(counted); } finally { setSaving(false); } }}
        >
          {counted ? "⊘" : "+"}
        </button>
      </CardHead>

      {open && (
        <div className="raster-reason">
          {!processed ? (
            <div className="raster-reason__none">{t("raster.not_processed")}</div>
          ) : reasoning.length > 0 ? (
            <ul className="raster-assess">
              {reasoning.map((c) => (
                <li key={c.criterion_key} className="raster-assess__item">
                  <div className="raster-assess__head">
                    <span className="raster-assess__label">{c.label}</span>
                    <span className="raster-assess__np">
                      {npLabel(c.suggested_np)}<small>{t("score.np_unit")}</small>
                    </span>
                  </div>
                  {c.reasoning && <p className="raster-assess__why">{c.reasoning}</p>}
                  {c.evidence_ids.length > 0 && (
                    <ul className="raster-reason__ev">
                      {c.evidence_ids.map((id) => {
                        const a = annById.get(id);
                        if (!a) return null;
                        return <ContentEvidence key={id} annotation={a} onPick={() => onPick(id)} />;
                      })}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="raster-reason__none">{t("raster.evidence_none")}</div>
          )}
        </div>
      )}

      <div className="raster-card__body">
        {parents.map((parent) => (
          <div key={parent} className="raster-card__subgroup">
            {multi && <div className="raster-card__subtitle">{parent}</div>}
            {rows
              .filter((r) => r.parent_label === parent)
              .map((r) => {
                const reason = reasonByKey.get(r.criterion_key);
                const evidenceIds = reason?.evidence_ids ?? [];
                const subKey = `sub:${r.task_id}:${r.criterion_key}`;
                return (
                  <SubRow
                    key={r.criterion_key}
                    rating={r}
                    onSave={onSave}
                    reasoning={reason?.reasoning ?? r.reasoning ?? null}
                    evidenceIds={evidenceIds}
                    annById={annById}
                    onPick={onPick}
                    open={openKey === subKey}
                    onToggle={() => onToggleSub(subKey, evidenceIds)}
                  />
                );
              })}
          </div>
        ))}
      </div>
    </div>
  );
}

// One deletable reason row (a LanguageTool flag). Delete → dismiss the
// annotation so its deduction drops and the criterion's suggestion rises; the
// dismissed flag stays struck-through with an undo.
function LanguageEvidence({ annotation: a, onPick }: { annotation: Annotation; onPick: () => void }) {
  const dismiss = useAnnotationStore((s) => s.dismiss);
  const restore = useAnnotationStore((s) => s.restore);
  const pending = useAnnotationStore((s) => s.pendingIds.has(a.id));
  const dismissed = a.status === "dismissed";

  return (
    <li className={"raster-ev" + (dismissed ? " raster-ev--dismissed" : "")}>
      <button type="button" className="raster-ev__text" onClick={onPick}>
        {a.original_text ? <em>{a.original_text}</em> : (a.rule_category ?? "—")}
        {a.suggested_text ? <> → <strong>{a.suggested_text}</strong></> : null}
        {a.point_deduction !== 0 && <span className="raster-ev__pt">−{a.point_deduction}</span>}
      </button>
      <ReasonAction dismissed={dismissed} pending={pending} onDelete={() => void dismiss(a.id)} onRestore={() => void restore(a.id)} />
    </li>
  );
}

// One deletable reason row (Claude content evidence). Same delete/undo
// semantics as language flags.
function ContentEvidence({ annotation: a, onPick }: { annotation: Annotation; onPick: () => void }) {
  const dismiss = useAnnotationStore((s) => s.dismiss);
  const restore = useAnnotationStore((s) => s.restore);
  const pending = useAnnotationStore((s) => s.pendingIds.has(a.id));
  const dismissed = a.status === "dismissed";

  return (
    <li className={"raster-ev" + (dismissed ? " raster-ev--dismissed" : "")}>
      <button type="button" className="raster-ev__text" onClick={onPick}>
        {a.content_note ?? a.original_text ?? "—"}
        {a.point_deduction !== 0 && <span className="raster-ev__pt">−{a.point_deduction}</span>}
      </button>
      <ReasonAction dismissed={dismissed} pending={pending} onDelete={() => void dismiss(a.id)} onRestore={() => void restore(a.id)} />
    </li>
  );
}

// Far-right delete (×) / undo (↩) control shared by both evidence kinds.
function ReasonAction({
  dismissed,
  pending,
  onDelete,
  onRestore,
}: {
  dismissed: boolean;
  pending: boolean;
  onDelete: () => void;
  onRestore: () => void;
}) {
  const { t } = useTranslation();
  return dismissed ? (
    <button
      type="button"
      className="raster-ev__btn raster-ev__btn--undo"
      disabled={pending}
      onClick={onRestore}
      title={t("raster.restore_reason")}
      aria-label={t("raster.restore_reason")}
    >
      ↩
    </button>
  ) : (
    <button
      type="button"
      className="raster-ev__btn raster-ev__btn--delete"
      disabled={pending}
      onClick={onDelete}
      title={t("raster.delete_reason")}
      aria-label={t("raster.delete_reason")}
    >
      ×
    </button>
  );
}

// One editable sub-criterion slot: a reset-to-suggestion control, the 0–15 NP
// field (pre-filled with the suggested value), and an expander (controlled by the
// panel so one reasoning panel is open at a time) that shows Claude's reasoning +
// supporting evidence (content).
function SubRow({
  rating,
  onSave,
  reasoning,
  evidenceIds,
  annById,
  onPick,
  open,
  onToggle,
}: {
  rating: CriterionRating;
  onSave: (body: SaveBody) => Promise<void>;
  reasoning?: string | null;
  evidenceIds?: number[];
  annById?: Map<number, Annotation>;
  onPick?: (id: number) => void;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const manual = rating.points_manual ?? null;
  // Pre-fill with the effective value (manual wins, else the suggestion) so the
  // suggested + curve-applied grade is always written in the field.
  const value = rating.points != null ? String(rating.points) : "";
  const overridden = manual != null;

  async function setManual(v: number | null) {
    setSaving(true);
    try {
      await onSave({ criterion_key: rating.criterion_key, task_id: rating.task_id, points_manual: v });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="raster-row">
      <div className="raster-row__main">
        <button
          type="button"
          className="raster-row__label"
          onClick={onToggle}
          title={t("raster.reason_title")}
        >
          <span className="raster-row__chevron">{open ? "▼" : "▶"}</span>
          {rating.label}
        </button>

        <div className="raster-row__controls">
          <button
            type="button"
            className={"raster-row__reset" + (overridden ? "" : " raster-row__reset--hidden")}
            onClick={() => void setManual(null)}
            disabled={saving || !overridden}
            title={t("raster.reset_suggested")}
            aria-label={t("raster.reset_suggested")}
          >
            ↺
          </button>
          <select
            className={"raster-row__select" + (overridden ? " raster-row__select--manual" : "")}
            value={value}
            disabled={saving}
            onChange={(e) => void setManual(e.target.value === "" ? null : Number(e.target.value))}
          >
            <option value="">{t("raster.unrated")}</option>
            {Array.from({ length: 16 }, (_, i) => 15 - i).map((np) => (
              <option key={np} value={np}>
                {np}
              </option>
            ))}
          </select>
        </div>
      </div>

      {open && (
        <div className="raster-row__detail">
          {reasoning ? <p className="raster-row__why">{reasoning}</p> : null}
          {evidenceIds && evidenceIds.length > 0 && annById && onPick && (
            <ul className="raster-reason__ev raster-row__ev">
              {evidenceIds.map((id) => {
                const a = annById.get(id);
                if (!a) return null;
                return <ContentEvidence key={id} annotation={a} onPick={() => onPick(id)} />;
              })}
            </ul>
          )}
          {!reasoning && !(evidenceIds && evidenceIds.length) && (
            <p className="raster-reason__none">{t("raster.no_reasoning")}</p>
          )}
        </div>
      )}
    </div>
  );
}
